import 'utils/moment-locale'
import React from 'react'
import { Text, Platform, View, Keyboard } from 'react-native'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindingTypes } from '@adalo/constants'
import DateTimePickerModal from 'react-native-modal-datetime-picker'

import { getScreenSize } from 'utils/responsive'
import {
  fromJSDateToJSDateStartOfDay,
  fromJSDateToJSDateStartOfMinute,
  fromJSDateToStringStartOfDay,
  fromJSDateToStringStartOfMinute,
  fromJSDateToLocaleString,
  toJSDate,
} from 'utils/luxon'
import { evaluateBinding } from '../../utils/dependencies'

import { changeValue, setDefaultValue, getValue } from '../../ducks/formInputs'

import BaseObject from '../BaseObject'
import ActionWrapper from '../ActionWrapper'

const defaults = {
  PLACEHOLDER_COLOR: '#9e9e9e',
}

class Picker extends BaseObject {
  static contextTypes = {
    getApp: PropTypes.func,
  }

  state = {
    isDatePickerOpen: false,
  }

  keyboardWillShowListener = null

  keyboardWillHideListener = null

  /**
   *
   * @returns {boolean}
   */
  getShowTime = () => {
    const { object } = this.props
    const { datePickerStyle } = object.attributes

    return !datePickerStyle || datePickerStyle === 'date'
  }

  /**
   *
   * @param {Date} dateObj
   * @returns
   */
  handleChange = dateObj => {
    if (!dateObj) {
      return
    }

    try {
      const { object, changeValue } = this.props

      changeValue(
        object.id,
        this.getShowTime()
          ? fromJSDateToStringStartOfMinute(dateObj)
          : fromJSDateToStringStartOfDay(dateObj)
      )
    } catch {
      // no-op
    }
  }

  hasDateBinding = (props = this.props) => {
    const { object } = props

    const hasDateDataBinding =
      object.dataBinding &&
      object.dataBinding.bindingType === bindingTypes.SET_DATE

    return hasDateDataBinding
  }

  getDefaultValue = (props = this.props) => {
    const { bindingData, defaultDate } = props

    if (defaultDate) {
      return defaultDate
    }

    if (this.hasDateBinding(props) && bindingData) {
      return bindingData
    }

    return ''
  }

  componentDidMount() {
    const { object, setDefaultValue } = this.props

    const defaultValue = this.getDefaultValue()

    setDefaultValue(object.id, defaultValue)

    if (Platform.OS === 'ios') {
      const windowHeight = getScreenSize().height || 625
      this.setState({
        iosHeight: windowHeight,
      })
    }

    this.keyboardWillShowListener = Keyboard.addListener(
      'keyboardWillShow',
      this.keyboardShow
    )
    this.keyboardWillHideListener = Keyboard.addListener(
      'keyboardWillHide',
      this.keyboardHide
    )
  }

  componentWillUnmount() {
    this.keyboardWillShowListener.remove()
    this.keyboardWillHideListener.remove()
  }

  componentDidUpdate(prevProps) {
    const { object, bindingData, setDefaultValue, value } = this.props

    const defaultValue = this.getDefaultValue()

    if (value !== prevProps.value) {
      // check if date picker has a value, otherwise set a default value
      if (!value) {
        setDefaultValue(object.id, defaultValue)
      }
    }

    if (!value && !prevProps.active && this.props.active) {
      if (defaultValue) {
        setDefaultValue(object.id, defaultValue)
      }
    }

    if (
      this.hasDateBinding() &&
      bindingData &&
      bindingData !== prevProps.bindingData
    ) {
      setDefaultValue(object.id, bindingData)
    }
  }

  openDatePicker = () => {
    const { value } = this.props

    if (!value && Platform.OS === 'ios') {
      this.handleChange(new Date())
    }

    this.setState({ isDatePickerOpen: true })
  }

  closeDatePicker = () => {
    this.setState({ isDatePickerOpen: false })
  }

  handleConfirm = date => {
    this.closeDatePicker()
    this.handleChange(date)
  }

  keyboardShow = e => {
    let { keyboardHeight } = this.state
    const newKeyboardHeight = e.endCoordinates && e.endCoordinates.height
    if (keyboardHeight !== newKeyboardHeight) {
      keyboardHeight = newKeyboardHeight
    }
    this.setState({ keyboardHeight, keyboardOpen: true })
  }

  keyboardHide = e => {
    this.setState({ keyboardOpen: false })
  }

  render() {
    const { object, component, bindingData, value } = this.props
    const { attributes, layout } = object
    const { datePickerStyle } = attributes

    let mode = 'date'
    let datepickerVariation = 'date'

    if (datePickerStyle === 'date' || datePickerStyle === undefined) {
      mode = 'datetime'
      datepickerVariation = 'datetime'
    }

    if (datePickerStyle === 'dateTextInput') {
      datepickerVariation = 'date-text'
    }

    let date = new Date()

    if (value) {
      date = toJSDate(value)
    } else {
      date = this.getShowTime()
        ? fromJSDateToJSDateStartOfMinute(date)
        : fromJSDateToJSDateStartOfDay(date)
    }

    const bgStyles = this.backgroundStyles()
    const textStyles = this.textStyles()
    const isPlaceholder = value
      ? { color: `${textStyles.color}` }
      : { color: `${defaults.PLACEHOLDER_COLOR}` }

    // check if background color exists, otherwise default to white
    bgStyles.backgroundColor = bgStyles.backgroundColor || 'white'

    const displayDate = fromJSDateToLocaleString(date, datepickerVariation)
    const { isDatePickerOpen, keyboardHeight, keyboardOpen, iosHeight } =
      this.state

    const pickerHeight = mode === 'datetime' ? 325 : 300

    const marginNoKeyboard = iosHeight / 2 - pickerHeight / 2
    const marginKeyboard = Math.max(marginNoKeyboard, keyboardHeight)

    // Set custom styles for iOS modal. Set everywhere but only affects iOS.
    const pickerContainerStyleIOS = {
      paddingTop: 8,
    }
    const modalStyleIOS = {
      width: 300,
      marginLeft: 'auto',
      marginRight: 'auto',
      marginBottom: keyboardOpen ? marginKeyboard : marginNoKeyboard,
    }
    const pickerStyleIOS = {
      height: pickerHeight,
      marginLeft: 8,
      marginRight: 8,
    }

    return (
      <ActionWrapper
        style={[layout, this.getOpacity()]}
        component={component}
        object={object}
        bindingData={bindingData}
      >
        <Text
          style={{
            width: '100%',
            paddingLeft: attributes.padding,
            height: attributes.height,
            paddingTop: Platform.OS === 'ios' ? attributes.height / 4 : 0,
            textAlignVertical: 'center',
            alignItems: 'center',
            ...bgStyles,
            ...this.borderStyles(),
            ...this.shadowStyles(),
            ...textStyles,
            ...isPlaceholder,
          }}
          onPress={this.openDatePicker}
        >
          {displayDate}
        </Text>

        <DateTimePickerModal
          isVisible={isDatePickerOpen}
          date={date}
          mode={mode}
          is24Hour={false}
          attributes={attributes}
          onCancel={this.closeDatePicker}
          onConfirm={this.handleConfirm}
          onChange={this.handleChange}
          display={Platform.OS === 'ios' ? 'inline' : 'default'}
          hideHeaderIOS
          pickerContainerStyleIOS={pickerContainerStyleIOS}
          modalStyleIOS={modalStyleIOS}
          style={pickerStyleIOS}
          customCancelButtonIOS={() => <View />}
          customConfirmButtonIOS={() => <View />}
        />
      </ActionWrapper>
    )
  }
}

const mapStateToProps = (state, props) => ({
  value: getValue(state, props.object.id),
  defaultDate: evaluateBinding(
    state,
    props.object.attributes.defaultDate,
    props
  ),
})

export default connect(mapStateToProps, { changeValue, setDefaultValue })(
  Picker
)
